import type {
  BrowserDisplayCaptureMode,
  BrowserDisplayIceCandidate,
  BrowserDisplaySession,
  BrowserUserId,
} from "@cmmd-forge/contracts";

const WEBRTC_MAX_CAPTURE_DEVICE_PIXEL_RATIO = 2;

export class BrowserWebRtcNegotiationTimeoutError extends Error {
  constructor(timeoutMs: number) {
    super(
      `Remote WebRTC browser display negotiation timed out before a video answer was ready after ${timeoutMs}ms.`,
    );
    this.name = "BrowserWebRtcNegotiationTimeoutError";
  }
}

export function browserErrorIsWebRtcNegotiationTimeout(
  error: unknown,
): error is BrowserWebRtcNegotiationTimeoutError {
  return error instanceof BrowserWebRtcNegotiationTimeoutError;
}

export function withBrowserWebRtcNegotiationTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  signal: AbortSignal,
): Promise<T> {
  if (signal.aborted) {
    return Promise.reject(new DOMException("Aborted.", "AbortError"));
  }
  return new Promise<T>((resolve, reject) => {
    let settled = false;
    const finish = (callback: () => void) => {
      if (settled) {
        return;
      }
      settled = true;
      window.clearTimeout(timeout);
      signal.removeEventListener("abort", handleAbort);
      callback();
    };
    const handleAbort = () => {
      finish(() => reject(new DOMException("Aborted.", "AbortError")));
    };
    const timeout = window.setTimeout(() => {
      finish(() => reject(new BrowserWebRtcNegotiationTimeoutError(timeoutMs)));
    }, timeoutMs);
    signal.addEventListener("abort", handleAbort, { once: true });
    promise.then(
      (value) => finish(() => resolve(value)),
      (error: unknown) => finish(() => reject(error)),
    );
  });
}

function browserWebRtcTerminalDisplay(
  input: {
    readonly viewport?: { readonly width: number; readonly height: number } | undefined;
    readonly devicePixelRatio?: number | undefined;
    readonly iceServers?: BrowserDisplaySession["iceServers"] | undefined;
  } & Pick<BrowserDisplaySession, "status" | "reason" | "detail">,
): BrowserDisplaySession {
  return {
    transport: "remote-webrtc",
    status: input.status,
    reason: input.reason,
    detail: input.detail,
    ...(input.viewport ? { viewport: input.viewport } : {}),
    ...(input.devicePixelRatio ? { devicePixelRatio: input.devicePixelRatio } : {}),
    ...(input.iceServers ? { iceServers: input.iceServers } : {}),
    reconnect: { status: "exhausted", attempt: 0 },
  };
}

export function browserWebRtcUnsupportedDisplay(input: {
  readonly viewport?: { readonly width: number; readonly height: number } | undefined;
  readonly devicePixelRatio?: number | undefined;
  readonly iceServers?: BrowserDisplaySession["iceServers"] | undefined;
}): BrowserDisplaySession {
  return browserWebRtcTerminalDisplay({
    ...input,
    status: "unsupported",
    reason: "unsupported_host",
    detail:
      "This browser host does not support RTCPeerConnection, so live WebRTC display cannot attach.",
  });
}

export function browserWebRtcNoTrackDisplay(input: {
  readonly viewport?: { readonly width: number; readonly height: number } | undefined;
  readonly devicePixelRatio?: number | undefined;
  readonly iceServers?: BrowserDisplaySession["iceServers"] | undefined;
}): BrowserDisplaySession {
  return browserWebRtcTerminalDisplay({
    ...input,
    status: "failed",
    reason: "unknown",
    detail: "Remote WebRTC browser display attached, but no video track arrived.",
  });
}

export function browserWebRtcNonRenderableTrackDisplay(input: {
  readonly viewport?: { readonly width: number; readonly height: number } | undefined;
  readonly devicePixelRatio?: number | undefined;
  readonly iceServers?: BrowserDisplaySession["iceServers"] | undefined;
}): BrowserDisplaySession {
  return browserWebRtcTerminalDisplay({
    ...input,
    status: "failed",
    reason: "unknown",
    detail: "Remote WebRTC browser display received a video track, but it never became renderable.",
  });
}

export function browserWebRtcNegotiationTimeoutDisplay(input: {
  readonly viewport?: { readonly width: number; readonly height: number } | undefined;
  readonly devicePixelRatio?: number | undefined;
  readonly iceServers?: BrowserDisplaySession["iceServers"] | undefined;
}): BrowserDisplaySession {
  return browserWebRtcTerminalDisplay({
    ...input,
    status: "failed",
    reason: "unknown",
    detail: "Remote WebRTC browser display negotiation timed out before a video answer was ready.",
  });
}

export function browserErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message.trim();
  }
  if (typeof error === "string" && error.trim().length > 0) {
    return error.trim();
  }
  return "Unknown error.";
}

export function browserWebRtcAttachFailureDisplay(input: {
  readonly viewport?: { readonly width: number; readonly height: number } | undefined;
  readonly devicePixelRatio?: number | undefined;
  readonly iceServers?: BrowserDisplaySession["iceServers"] | undefined;
  readonly error: unknown;
}): BrowserDisplaySession {
  return browserWebRtcTerminalDisplay({
    ...input,
    status: "failed",
    reason: "unknown",
    detail: `Remote WebRTC browser display attach failed: ${browserErrorMessage(input.error)}`,
  });
}

export function browserWebRtcConnectingDisplay(input: {
  readonly viewport?: { readonly width: number; readonly height: number } | undefined;
  readonly devicePixelRatio?: number | undefined;
  readonly iceServers?: BrowserDisplaySession["iceServers"] | undefined;
}): BrowserDisplaySession {
  return {
    transport: "remote-webrtc",
    status: "connecting",
    detail: "Remote WebRTC browser display is negotiating a live video transport.",
    ...(input.viewport ? { viewport: input.viewport } : {}),
    ...(input.devicePixelRatio ? { devicePixelRatio: input.devicePixelRatio } : {}),
    ...(input.iceServers ? { iceServers: input.iceServers } : {}),
    reconnect: { status: "ready", attempt: 0 },
  };
}

export function browserWebRtcNetworkFailureDisplay(input: {
  readonly viewport?: { readonly width: number; readonly height: number } | undefined;
  readonly devicePixelRatio?: number | undefined;
  readonly iceServers?: BrowserDisplaySession["iceServers"] | undefined;
  readonly detail?: string | undefined;
}): BrowserDisplaySession {
  return browserWebRtcTerminalDisplay({
    ...input,
    status: "failed",
    reason: "network_unreachable",
    detail:
      input.detail ??
      "Remote WebRTC browser display media could not route. Configure a TURN relay for cloud browser display.",
  });
}

export function browserWebRtcReconnectingDisplay(input: {
  readonly viewport?: { readonly width: number; readonly height: number } | undefined;
  readonly devicePixelRatio?: number | undefined;
  readonly iceServers?: BrowserDisplaySession["iceServers"] | undefined;
  readonly reason?: BrowserDisplaySession["reason"] | undefined;
  readonly detail: string;
  readonly attempt: number;
  readonly delayMs: number;
}): BrowserDisplaySession {
  return {
    transport: "remote-webrtc",
    status: "reconnecting",
    ...(input.reason ? { reason: input.reason } : {}),
    detail: input.detail,
    ...(input.viewport ? { viewport: input.viewport } : {}),
    ...(input.devicePixelRatio ? { devicePixelRatio: input.devicePixelRatio } : {}),
    ...(input.iceServers ? { iceServers: input.iceServers } : {}),
    reconnect: {
      status: "scheduled",
      attempt: input.attempt,
      nextRetryAt: new Date(Date.now() + input.delayMs).toISOString(),
    },
  };
}

export function browserDisplayWithReconnect(
  display: BrowserDisplaySession,
  reconnect: NonNullable<BrowserDisplaySession["reconnect"]>,
): BrowserDisplaySession {
  return {
    ...display,
    reconnect,
  };
}

export function browserWebRtcReconnectDelayMs(input: {
  readonly attempt: number;
  readonly baseDelayMs: number;
  readonly maxDelayMs: number;
}): number {
  return Math.min(input.baseDelayMs * 2 ** Math.max(0, input.attempt - 1), input.maxDelayMs);
}

export function resolveBrowserWebRtcReconnectDecision(input: {
  readonly currentAttempt: number;
  readonly maxReconnectAttempts: number;
  readonly baseDelayMs: number;
  readonly maxDelayMs: number;
}):
  | {
      readonly kind: "exhausted";
      readonly attempt: number;
    }
  | {
      readonly kind: "schedule";
      readonly attempt: number;
      readonly delayMs: number;
    } {
  const nextAttempt = input.currentAttempt + 1;
  if (nextAttempt > input.maxReconnectAttempts) {
    return {
      kind: "exhausted",
      attempt: input.currentAttempt,
    };
  }
  return {
    kind: "schedule",
    attempt: nextAttempt,
    delayMs: browserWebRtcReconnectDelayMs({
      attempt: nextAttempt,
      baseDelayMs: input.baseDelayMs,
      maxDelayMs: input.maxDelayMs,
    }),
  };
}

export function browserWebRtcPeerMediaCanFlow(input: {
  readonly connectionState: RTCPeerConnectionState;
  readonly iceConnectionState: RTCIceConnectionState;
}): boolean {
  return (
    input.connectionState === "connected" ||
    input.iceConnectionState === "connected" ||
    input.iceConnectionState === "completed"
  );
}

export function browserWebRtcShouldFailNegotiationTimeout(input: {
  readonly aborted: boolean;
  readonly failed: boolean;
  readonly trackReceived: boolean;
}): boolean {
  return !input.aborted && !input.failed && !input.trackReceived;
}

export function browserWebRtcShouldScheduleConnectivityTimeout(input: {
  readonly aborted: boolean;
  readonly failed: boolean;
  readonly attachAccepted: boolean;
  readonly hasAcceptedDisplay: boolean;
  readonly connectionState: RTCPeerConnectionState;
  readonly iceConnectionState: RTCIceConnectionState;
}): boolean {
  return (
    !input.aborted &&
    !input.failed &&
    input.attachAccepted &&
    input.hasAcceptedDisplay &&
    !browserWebRtcPeerMediaCanFlow(input)
  );
}

export function browserWebRtcIceDiagnosticsDetail(input: {
  readonly prefix: string;
  readonly connectionState: RTCPeerConnectionState;
  readonly iceConnectionState: RTCIceConnectionState;
  readonly localCandidatesSubmitted: number;
  readonly localCandidatesReal: number;
  readonly localEndOfCandidates: number;
  readonly localCandidateTypeCounts: ReadonlyMap<string, number>;
  readonly remoteCandidatesSeen: number;
  readonly remoteCandidateTypeCounts: ReadonlyMap<string, number>;
  readonly remoteCandidatesPending: number;
}): string {
  return `${input.prefix} connectionState=${input.connectionState}; iceConnectionState=${input.iceConnectionState}; localCandidatesSubmitted=${input.localCandidatesSubmitted}; localCandidatesReal=${input.localCandidatesReal}; localEndOfCandidates=${input.localEndOfCandidates}; localCandidateTypes=${browserIceCandidateTypeSummary(input.localCandidateTypeCounts)}; remoteCandidatesSeen=${input.remoteCandidatesSeen}; remoteCandidateTypes=${browserIceCandidateTypeSummary(input.remoteCandidateTypeCounts)}; remoteCandidatesPending=${input.remoteCandidatesPending}.`;
}

export function browserWebRtcVideoClassState(input: {
  readonly hasActiveVideoStream: boolean;
  readonly videoElementHasActiveStream: boolean;
}): {
  readonly visibilityClass: "block" | "hidden";
  readonly opacityClass: "opacity-100" | "opacity-0";
} {
  return {
    visibilityClass: input.hasActiveVideoStream ? "block" : "hidden",
    opacityClass:
      input.hasActiveVideoStream && input.videoElementHasActiveStream ? "opacity-100" : "opacity-0",
  };
}

export function browserWebRtcVideoAspectRatio(
  viewport: { readonly width: number; readonly height: number } | undefined,
): string {
  return viewport ? `${viewport.width} / ${viewport.height}` : "16 / 9";
}

export function resolveBrowserWebRtcDevicePixelRatio(
  explicitDevicePixelRatio: number | undefined,
): number | undefined {
  const candidate =
    explicitDevicePixelRatio ??
    (typeof window === "undefined" ? undefined : window.devicePixelRatio);
  if (typeof candidate !== "number" || !Number.isFinite(candidate) || candidate <= 0) {
    return undefined;
  }
  return Math.min(Math.max(1, candidate), WEBRTC_MAX_CAPTURE_DEVICE_PIXEL_RATIO);
}

export function browserStreamHasLiveVideoTrack(stream: MediaStream): boolean {
  return stream.getVideoTracks().some((track) => track.readyState === "live");
}

export function browserVideoElementHasRenderableStream(
  videoElement: HTMLVideoElement,
  stream: MediaStream,
): boolean {
  return (
    videoElement.srcObject === stream &&
    browserStreamHasLiveVideoTrack(stream) &&
    videoElement.readyState >= HTMLMediaElement.HAVE_METADATA &&
    videoElement.videoWidth > 0 &&
    videoElement.videoHeight > 0
  );
}

export function toRtcIceServers(
  iceServers: BrowserDisplaySession["iceServers"] | undefined,
): RTCIceServer[] {
  const rtcIceServers: RTCIceServer[] = [];
  for (const server of iceServers ?? []) {
    const rtcIceServer: RTCIceServer = {
      urls: typeof server.urls === "string" ? server.urls : [...server.urls],
    };
    if (server.username) {
      rtcIceServer.username = server.username;
    }
    if (server.credential !== undefined) {
      rtcIceServer.credential = server.credential;
    }
    rtcIceServers.push(rtcIceServer);
  }
  return rtcIceServers;
}

export function browserDisplayIceServerUrls(
  server: NonNullable<BrowserDisplaySession["iceServers"]>[number],
): readonly string[] {
  return typeof server.urls === "string" ? [server.urls] : server.urls;
}

export function browserDisplayIceServerUrlAllowsUdpRelay(url: string): boolean {
  const [schemeAndAddress = "", query = ""] = url.trim().split("?", 2);
  const normalizedSchemeAndAddress = schemeAndAddress.toLowerCase();
  if (normalizedSchemeAndAddress.startsWith("turns:")) {
    return new URLSearchParams(query).get("transport")?.toLowerCase() === "udp";
  }
  if (!normalizedSchemeAndAddress.startsWith("turn:")) {
    return false;
  }
  const transport = new URLSearchParams(query).get("transport")?.toLowerCase();
  return transport === undefined || transport === "udp";
}

export function browserDisplayIceServersShouldForceClientRelay(
  iceServers: BrowserDisplaySession["iceServers"] | undefined,
): boolean {
  return (iceServers ?? []).some(
    (server) =>
      Boolean(server.username) &&
      Boolean(server.credential) &&
      browserDisplayIceServerUrls(server).some(browserDisplayIceServerUrlAllowsUdpRelay),
  );
}

export function browserIceServersKey(
  iceServers: BrowserDisplaySession["iceServers"] | undefined,
): string {
  return JSON.stringify(
    (iceServers ?? []).map((server) => ({
      urls: typeof server.urls === "string" ? server.urls : [...server.urls],
      username: server.username ?? null,
      credential: server.credential ?? null,
    })),
  );
}

export function browserIceServersFromKey(
  key: string,
): BrowserDisplaySession["iceServers"] | undefined {
  const iceServers = JSON.parse(key) as NonNullable<BrowserDisplaySession["iceServers"]>;
  return iceServers.length === 0 ? undefined : iceServers;
}

export function toRtcIceCandidate(
  candidate: BrowserDisplayIceCandidate | null,
): RTCIceCandidateInit | null {
  if (candidate === null) {
    return null;
  }
  return {
    candidate: candidate.candidate,
    ...(candidate.sdpMid !== undefined ? { sdpMid: candidate.sdpMid } : {}),
    ...(candidate.sdpMLineIndex !== undefined ? { sdpMLineIndex: candidate.sdpMLineIndex } : {}),
    ...(candidate.usernameFragment !== undefined
      ? { usernameFragment: candidate.usernameFragment }
      : {}),
  };
}

export function browserIceCandidateType(candidate: BrowserDisplayIceCandidate): string {
  return candidate.candidate.match(/(?:^|\s)typ\s+([^\s]+)/)?.[1] ?? "unknown";
}

export function browserIceCandidateTypeSummary(counts: ReadonlyMap<string, number>): string {
  const entries = [...counts.entries()].toSorted(([left], [right]) => left.localeCompare(right));
  return entries.length === 0
    ? "none"
    : entries.map(([type, count]) => `${type}:${count}`).join(",");
}

/**
 * The optional half of a display-attach payload.
 *
 * Every one of these fields is `optionalKey` in the contract: present means
 * "use this", absent means "use the server's default", and `undefined` is not
 * the same as either — it fails decoding. So each one needs its own presence
 * check, and four of them inline in `attach` is four branches in a function
 * that is already the most complex in this file.
 *
 * Pulled out so the negotiation reads as negotiation, and so the presence rule
 * is asserted once here rather than re-derived at each call site.
 */
export function browserDisplayAttachOptionalFields(input: {
  readonly userId?: BrowserUserId | null | undefined;
  readonly captureMode?: BrowserDisplayCaptureMode | undefined;
  readonly viewport?: { readonly width: number; readonly height: number } | undefined;
  readonly devicePixelRatio?: number | undefined;
}): {
  readonly userId?: BrowserUserId;
  readonly captureMode?: BrowserDisplayCaptureMode;
  readonly viewport?: { readonly width: number; readonly height: number };
  readonly devicePixelRatio?: number;
} {
  return {
    ...(input.userId ? { userId: input.userId } : {}),
    ...(input.captureMode ? { captureMode: input.captureMode } : {}),
    ...(input.viewport ? { viewport: input.viewport } : {}),
    ...(input.devicePixelRatio ? { devicePixelRatio: input.devicePixelRatio } : {}),
  };
}
